import { useState } from "react";
import { Loader2, BookOpen, Search } from "lucide-react";
import { useMayor, useCuentas } from "../api";
import { useAccountingStore } from "../store";
import { AccountingHeader } from "../components/AccountingHeader";

function eur(n: number): string {
  return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n);
}

export function MayorPage() {
  const { emisorId, ejercicio } = useAccountingStore();
  const [cuenta, setCuenta] = useState<string>("");
  const { data: cuentas } = useCuentas(emisorId, { activa: true });
  const { data: mayor, isLoading, isFetching } = useMayor(emisorId, cuenta || null, ejercicio);

  return (
    <div className="p-8 h-full flex flex-col gap-5 min-h-0">
      <AccountingHeader
        title="Contabilidad"
        subtitle="Libro mayor: movimientos y saldo de una cuenta."
      />

      <div className="shrink-0">
        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1">
          Cuenta
        </label>
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            list="mayor-cuentas"
            value={cuenta}
            onChange={(e) => setCuenta(e.target.value)}
            placeholder="Escribe o selecciona un código de cuenta…"
            className="w-full rounded-lg border border-slate-300 pl-9 pr-3 py-2 text-sm font-mono"
          />
          <datalist id="mayor-cuentas">
            {cuentas?.map((c) => (
              <option key={c.id} value={c.codigo}>
                {c.codigo} · {c.nombre}
              </option>
            ))}
          </datalist>
        </div>
      </div>

      {!cuenta && (
        <div className="flex-1 flex items-center justify-center text-slate-400">
          <div className="text-center">
            <BookOpen size={40} className="mx-auto mb-2 opacity-40" />
            <p className="font-semibold">Selecciona una cuenta para ver su mayor.</p>
          </div>
        </div>
      )}

      {cuenta && (isLoading || isFetching) && (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="animate-spin" size={18} /> Cargando movimientos…
        </div>
      )}

      {cuenta && mayor && !isFetching && (
        <>
          {/* Cabecera de saldos */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 shrink-0">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-soft">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Cuenta
              </div>
              <div className="text-lg font-black text-slate-900 mt-1 font-mono">
                {mayor.cuenta.codigo}
              </div>
              <div className="text-xs text-slate-500 truncate">{mayor.cuenta.nombre}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-soft">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Saldo apertura
              </div>
              <div className="text-lg font-black text-slate-900 mt-1 font-mono">
                {eur(mayor.saldo_apertura)}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-soft">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Movimientos
              </div>
              <div className="text-lg font-black text-slate-900 mt-1">
                {mayor.movimientos.length}
              </div>
              <div className="text-xs text-slate-500 font-mono">
                D {eur(mayor.total_debe)} · H {eur(mayor.total_haber)}
              </div>
            </div>
            <div className="rounded-2xl border border-brand-700/20 bg-gradient-to-br from-brand-700 to-emerald-600 text-white p-4 shadow-soft">
              <div className="text-xs font-bold uppercase tracking-wider text-emerald-100">
                Saldo final
              </div>
              <div className="text-lg font-black mt-1 font-mono">{eur(mayor.saldo_final)}</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-soft border border-slate-200/70 overflow-hidden flex flex-col">
            <div className="overflow-auto max-h-[calc(100vh-460px)]">
              <table className="w-full">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 sticky top-0 z-10">
                  <tr>
                    <th className="text-left px-4 py-3 font-bold w-16">Asto</th>
                    <th className="text-left px-4 py-3 font-bold w-28">Fecha</th>
                    <th className="text-left px-4 py-3 font-bold">Concepto / Descripción</th>
                    <th className="text-right px-4 py-3 font-bold w-32">Debe</th>
                    <th className="text-right px-4 py-3 font-bold w-32">Haber</th>
                    <th className="text-right px-4 py-3 font-bold w-32">Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-slate-50/40 border-t border-slate-100">
                    <td colSpan={5} className="px-4 py-2 text-sm font-bold text-slate-500 text-right">
                      Saldo de apertura
                    </td>
                    <td className="px-4 py-2 text-right font-mono font-bold">
                      {eur(mayor.saldo_apertura)}
                    </td>
                  </tr>
                  {mayor.movimientos.map((m, i) => (
                    <tr key={i} className="border-t border-slate-100 hover:bg-slate-50/60">
                      <td className="px-4 py-2 font-mono text-sm text-slate-500">{m.asiento_id}</td>
                      <td className="px-4 py-2 text-sm text-slate-600">
                        {new Date(m.fecha).toLocaleDateString("es-ES")}
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <div className="font-semibold text-slate-800">{m.concepto}</div>
                        {m.descripcion && (
                          <div className="text-xs text-slate-500">{m.descripcion}</div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right font-mono">
                        {m.debe > 0 ? eur(m.debe) : "—"}
                      </td>
                      <td className="px-4 py-2 text-right font-mono">
                        {m.haber > 0 ? eur(m.haber) : "—"}
                      </td>
                      <td className="px-4 py-2 text-right font-mono font-bold text-slate-900">
                        {eur(m.saldo)}
                      </td>
                    </tr>
                  ))}
                  {mayor.movimientos.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                        Sin movimientos en {ejercicio}.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
